import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus } from '@phosphor-icons/react';
import Layout from '../../components/layout/Layout';
import { profileAPI } from '../../utils/api';

export default function AddManualSyllabusPage() {
  const navigate = useNavigate();
  const { courseId } = useParams<{ courseId: string }>();
  const [formData, setFormData] = useState({
    topics: [] as string[],
    prerequisites: [] as string[],
    learning_objectives: [] as string[],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Input state for dynamic arrays
  const [topicInput, setTopicInput] = useState('');
  const [prerequisiteInput, setPrerequisiteInput] = useState('');
  const [objectiveInput, setObjectiveInput] = useState('');

  useEffect(() => {
    if (!courseId) {
      navigate('/dashboard');
    }
  }, [courseId, navigate]);

  const addToArray = (field: 'topics' | 'prerequisites' | 'learning_objectives', value: string, setValue: (value: string) => void) => {
    if (value.trim()) {
      setFormData(prev => ({
        ...prev,
        [field]: [...prev[field], value.trim()]
      }));
      setValue('');
    }
  };

  const removeFromArray = (field: 'topics' | 'prerequisites' | 'learning_objectives', index: number) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.topics.length === 0) {
      setError('At least one topic is required');
      return;
    }

    if (!courseId) {
      setError('Course ID is missing');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const submitData = {
        course_id: courseId,
        topics: formData.topics,
        prerequisites: formData.prerequisites.length > 0 ? formData.prerequisites : undefined,
        learning_objectives: formData.learning_objectives.length > 0 ? formData.learning_objectives : undefined,
      };

      await profileAPI.addManualSyllabus(submitData);
      
      // Navigate back to dashboard with refresh flag
      navigate('/dashboard', { state: { fromAddSubject: true } });
    } catch (err: any) {
      console.error('Failed to add manual syllabus:', err);
      setError(err.message || 'Failed to add syllabus');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="px-40 flex flex-1 justify-center py-5">
        <div className="layout-content-container flex flex-col max-w-[960px] flex-1">
          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={() => navigate('/dashboard')}
              className="text-white hover:text-text-secondary transition-colors"
            >
              <ArrowLeft size={24} weight="regular" />
            </button>
            <h1 className="text-white text-2xl font-bold">Add Course Syllabus</h1>
          </div>

          <p className="text-text-secondary mb-6">
            Please provide the syllabus information for your course. This will help us create personalized lessons tailored to your curriculum.
          </p>

          {error && (
            <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded mb-6">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Topics */}
            <div className="bg-input-bg border border-border-light rounded-lg p-6">
              <h2 className="text-white text-lg font-semibold mb-2">Course Topics *</h2>
              <p className="text-text-secondary text-sm mb-4">
                Add the main topics that will be covered in this course.
              </p>
              
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={topicInput}
                  onChange={(e) => setTopicInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToArray('topics', topicInput, setTopicInput))}
                  className="flex-1 px-3 py-2 bg-background border border-border-light rounded text-white placeholder-text-secondary focus:outline-none focus:border-primary"
                  placeholder="e.g., Introduction to Algebra"
                />
                <button
                  type="button"
                  onClick={() => addToArray('topics', topicInput, setTopicInput)}
                  className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90 transition-colors"
                >
                  <Plus size={16} />
                </button>
              </div>

              <div className="flex flex-wrap gap-2">
                {formData.topics.map((topic, index) => (
                  <span
                    key={index}
                    className="bg-primary/20 text-primary px-3 py-1 rounded-full text-sm flex items-center gap-2"
                  >
                    {topic}
                    <button
                      type="button"
                      onClick={() => removeFromArray('topics', index)}
                      className="text-primary hover:text-primary/70"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              
              {formData.topics.length === 0 && (
                <p className="text-text-secondary text-sm mt-2">
                  Please add at least one topic to continue.
                </p>
              )}
            </div>

            {/* Prerequisites */}
            <div className="bg-input-bg border border-border-light rounded-lg p-6">
              <h2 className="text-white text-lg font-semibold mb-2">Prerequisites (Optional)</h2>
              <p className="text-text-secondary text-sm mb-4">
                Add any prerequisite knowledge or courses needed before taking this course.
              </p>
              
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={prerequisiteInput}
                  onChange={(e) => setPrerequisiteInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToArray('prerequisites', prerequisiteInput, setPrerequisiteInput))}
                  className="flex-1 px-3 py-2 bg-background border border-border-light rounded text-white placeholder-text-secondary focus:outline-none focus:border-primary"
                  placeholder="e.g., Basic Arithmetic"
                />
                <button
                  type="button"
                  onClick={() => addToArray('prerequisites', prerequisiteInput, setPrerequisiteInput)}
                  className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90 transition-colors"
                >
                  <Plus size={16} />
                </button>
              </div>

              <div className="flex flex-wrap gap-2">
                {formData.prerequisites.map((prerequisite, index) => (
                  <span
                    key={index}
                    className="bg-secondary/20 text-secondary px-3 py-1 rounded-full text-sm flex items-center gap-2"
                  >
                    {prerequisite}
                    <button
                      type="button"
                      onClick={() => removeFromArray('prerequisites', index)}
                      className="text-secondary hover:text-secondary/70"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Learning Objectives */}
            <div className="bg-input-bg border border-border-light rounded-lg p-6">
              <h2 className="text-white text-lg font-semibold mb-2">Learning Objectives (Optional)</h2>
              <p className="text-text-secondary text-sm mb-4">
                Define what students should be able to do after completing this course.
              </p>
              
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={objectiveInput}
                  onChange={(e) => setObjectiveInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToArray('learning_objectives', objectiveInput, setObjectiveInput))}
                  className="flex-1 px-3 py-2 bg-background border border-border-light rounded text-white placeholder-text-secondary focus:outline-none focus:border-primary"
                  placeholder="e.g., Solve linear equations"
                />
                <button
                  type="button"
                  onClick={() => addToArray('learning_objectives', objectiveInput, setObjectiveInput)}
                  className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90 transition-colors"
                >
                  <Plus size={16} />
                </button>
              </div>

              <div className="flex flex-wrap gap-2">
                {formData.learning_objectives.map((objective, index) => (
                  <span
                    key={index}
                    className="bg-green-500/20 text-green-400 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                  >
                    {objective}
                    <button
                      type="button"
                      onClick={() => removeFromArray('learning_objectives', index)}
                      className="text-green-400 hover:text-green-400/70"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end gap-4">
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="px-6 py-2 bg-transparent border border-border-light text-white rounded hover:bg-input-bg transition-colors"
              >
                Skip for Now
              </button>
              <button
                type="submit"
                disabled={loading || formData.topics.length === 0}
                className="px-6 py-2 bg-primary text-white rounded hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Adding...' : 'Add Syllabus'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
}
